import type { PageState, Wall, Point, MapObject } from '@roll1/shared';
import { LIMITS } from '@roll1/shared';
import { computeVisibilityPolygon, isWallBlockingLight } from './visibility';
import { SpatialIndex } from './spatial-index';

// --- Runtime-only types (not serialized) ---

/** Унифицированный источник света для вычислений (не сериализуется) */
export interface LightSource {
  id: string;
  type: 'map-object' | 'token' | 'global';
  x: number;
  y: number;
  /** Bright radius in canvas pixels */
  brightRadius: number;
  /** Dim radius in canvas pixels */
  dimRadius: number;
  color?: string;
  sourceId: string;
}

/** Результат вычисления освещённой зоны одного источника */
export interface LitZone {
  sourceId: string;
  brightPolygon: Point[];
  dimPolygon: Point[];
}

// --- Helper: region center ---

function getRegionCenter(region: MapObject['region']): Point {
  switch (region.type) {
    case 'rect':
      return { x: region.x + region.width / 2, y: region.y + region.height / 2 };
    case 'circle':
      return { x: region.cx, y: region.cy };
    case 'polygon': {
      const pts = region.points;
      let cx = 0, cy = 0;
      for (const p of pts) { cx += p.x; cy += p.y; }
      return { x: cx / pts.length, y: cy / pts.length };
    }
    default:
      return { x: 0, y: 0 };
  }
}

// --- collectLightSources ---

/**
 * Собирает все источники света на странице.
 * Возвращает унифицированный массив LightSource[] (runtime-only, в canvas pixels).
 *
 * @param page - текущая страница
 * @param gridSize - размер клетки в canvas pixels
 * @param gridSizeFt - размер клетки в футах (обычно 5)
 * @returns массив LightSource (max MAX_LIGHT_SOURCES локальных + global)
 */
export function collectLightSources(
  page: PageState,
  gridSize: number,
  gridSizeFt: number,
): LightSource[] {
  const localSources: LightSource[] = [];
  const ftToPx = gridSize / gridSizeFt;

  // 1. MapObject light sources
  for (const obj of page.mapObjects ?? []) {
    if (obj.category !== 'light') continue;
    if (obj.properties.state === 'extinguished') continue;
    const lightProps = obj.properties.light;
    if (!lightProps || lightProps.radius <= 0) continue;

    const center = getRegionCenter(obj.region);
    localSources.push({
      id: `mo-${obj.id}`,
      type: 'map-object',
      x: center.x,
      y: center.y,
      brightRadius: lightProps.radius * ftToPx,
      dimRadius: lightProps.radius * 2 * ftToPx,
      color: lightProps.color,
      sourceId: obj.id,
    });
  }

  // 2. Token light sources
  for (const token of page.tokens) {
    if (!token.light?.enabled) continue;
    if (token.light.brightRadius <= 0 && token.light.dimRadius <= 0) continue;

    localSources.push({
      id: `tk-${token.id}`,
      type: 'token',
      x: token.x,
      y: token.y,
      brightRadius: token.light.brightRadius * ftToPx,
      dimRadius: token.light.dimRadius * ftToPx,
      color: token.light.color,
      sourceId: token.id,
    });
  }

  // 3. Enforce MAX_LIGHT_SOURCES for local sources
  if (localSources.length > LIMITS.MAX_LIGHT_SOURCES) {
    console.warn(
      `[lighting] ${localSources.length} local light sources exceed MAX_LIGHT_SOURCES (${LIMITS.MAX_LIGHT_SOURCES}). Truncating.`,
    );
    localSources.length = LIMITS.MAX_LIGHT_SOURCES;
  }

  // 4. Global illumination pseudo-source
  const gi = page.globalIllumination;
  if (gi.level === 'bright') {
    localSources.push({
      id: 'global',
      type: 'global',
      x: 0,
      y: 0,
      brightRadius: Infinity,
      dimRadius: Infinity,
      color: gi.color,
      sourceId: 'global',
    });
  } else if (gi.level === 'dim') {
    localSources.push({
      id: 'global',
      type: 'global',
      x: 0,
      y: 0,
      brightRadius: 0,
      dimRadius: Infinity,
      color: gi.color,
      sourceId: 'global',
    });
  }
  // dark: no global source

  return localSources;
}

// --- computeLitZones ---

/**
 * Вычисляет освещённые зоны (bright + dim polygons) для каждого источника.
 * Все координаты в world-space (canvas pixels).
 *
 * @param sources - массив LightSource из collectLightSources
 * @param walls - стены страницы
 * @param gridSize - размер клетки в canvas pixels
 * @param gridSizeFt - размер клетки в футах
 * @returns массив LitZone (один per source, global — special marker)
 */
export function computeLitZones(
  sources: LightSource[],
  walls: Wall[],
  _gridSize: number,
  _gridSizeFt: number,
): LitZone[] {
  // Pre-filter walls that block light
  const lightBlockingWalls = walls.filter(isWallBlockingLight);

  // Build spatial index for light-blocking walls
  const spatialIdx = new SpatialIndex(64);
  spatialIdx.build(lightBlockingWalls);

  const zones: LitZone[] = [];

  for (const source of sources) {
    if (source.type === 'global') {
      // Global source: page-wide polygon (signal to renderer)
      zones.push({
        sourceId: source.sourceId,
        brightPolygon: source.brightRadius === Infinity
          ? [{ x: -1e6, y: -1e6 }, { x: 1e6, y: -1e6 }, { x: 1e6, y: 1e6 }, { x: -1e6, y: 1e6 }]
          : [],
        dimPolygon: source.dimRadius === Infinity
          ? [{ x: -1e6, y: -1e6 }, { x: 1e6, y: -1e6 }, { x: 1e6, y: 1e6 }, { x: -1e6, y: 1e6 }]
          : [],
      });
      continue;
    }

    const origin: Point = { x: source.x, y: source.y };

    // Use spatial index for wall culling within dimRadius (the larger radius)
    const maxRadius = Math.max(source.brightRadius, source.dimRadius);
    const culledWalls = maxRadius > 0
      ? spatialIdx.query(origin.x, origin.y, maxRadius)
      : lightBlockingWalls;

    const brightPolygon = source.brightRadius > 0
      ? computeVisibilityPolygon(origin, culledWalls, source.brightRadius)
      : [];

    const dimPolygon = source.dimRadius > 0
      ? computeVisibilityPolygon(origin, culledWalls, source.dimRadius)
      : [];

    zones.push({
      sourceId: source.sourceId,
      brightPolygon,
      dimPolygon,
    });
  }

  return zones;
}
